import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Stack,
  Chip,
  Select,
  MenuItem,
  Portal,
  CircularProgress,
} from "@mui/material";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Delete, FileCopy, Save, ArrowUpward, ArrowDownward, ArrowBack as ArrowBackIcon, } from "@mui/icons-material";
import Grid from '@mui/material/Grid2';
import { ReactComponent as Generate} from '../../assets/svg/generate.svg';
import PopupMenu from "../PopupMenu/PopupMenu";
import Loader from "../../loader";
import { isSourceSentence } from './sourceMatch';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TemplateView from "../Settings/Template/TemplateView";
import CopyIcon from '@mui/icons-material/FileCopy';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import Split from 'react-split';
import { SpreadsheetGrid } from 'react-spreadsheet-grid';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import ListIcon from '@mui/icons-material/List';
import DescriptionIcon from '@mui/icons-material/Description';
import SophieScribeImage from '../../assets/images/Sophie Scribe.png';
import ResizeIcon from '../../assets/images/resize.png';
import CloseIcon from '@mui/icons-material/Close';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import HighlightIcon from '@mui/icons-material/Highlight';


// Custom gutter factory for react-split — thin vertical line + resize icon.
const createSplitGutter = (index, direction) => {
  const gut = document.createElement('div');
  gut.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'cursor:col-resize',
    'width:20px',
    'flex-shrink:0',
    'user-select:none',
    '-webkit-user-select:none',
    'position:relative',
  ].join(';');

  const line = document.createElement('div');
  line.style.cssText = [
    'position:absolute',
    'top:0',
    'bottom:0',
    'left:50%',
    'transform:translateX(-50%)',
    'width:1px',
    'background:#C4C4C4',
    'pointer-events:none',
  ].join(';');

  const icon = document.createElement('img');
  icon.src = ResizeIcon;
  icon.alt = 'resize';
  icon.style.cssText = [
    'position:relative',
    'z-index:1',
    'width:22px',
    'height:22px',
    'display:block',
    'opacity:0.45',
    'pointer-events:none',
  ].join(';');

  gut.appendChild(line);
  gut.appendChild(icon);
  return gut;
};

const ReportSummaryView = ({
  onTemplateSaved,
  patient_id,
  currentOrg,
  regenerateHistoryReport,
  setPopupPdfFill,
  handleDownloadItem,
  setHistoryItems,
  files,
  handleRetrieveText,
  retrievedData,
  selectedReport,
  setSelectedReport,
  handleCopy,
  extractParagraphText,
  setPopupReport,
  handleBackToTemplateSelector,
  userDetails,
  setSelectedText,
  selectedText,
  highlightMode,
  setHighlightMode,
  clearHighlights,
  chatMessages,
  newMessage,
  setNewMessage,
  handleSendMessage,
  isChatOpen,
  setIsChatOpen,
  chatBoxRef,
  answerSource,
  setAnswerSource,
  isAnswerSourceOpen,
  setIsAnswerSourceOpen,
  setAlert,
  responseSources,
  setResponseSources,
  openSource,
  setOpenSource,
  handleSaveChatMessageAsReport,
  templates,
  selectedTemplate,
  handleTemplateSelect,
  handleUploadTemplate,
  isGeneratingReport,
}) => {
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [numberOfHighlights, setNumberOfHighlights] = useState(0); 
  const [fileHighlightCounts, setFileHighlightCounts] = useState([]);
  const [retrieving, setRetrieving] = useState(false);
  const [content, setContent] = useState("");
  const hasMounted = useRef(false);
  const [highlightingProgress, setHighlightingProgress] = useState(false);
  const [reportTitle, setReportTitle] = useState("");
  const [highlightBtnPos, setHighlightBtnPos] = useState(null); // { x, y } for floating highlight button
  const [pendingHighlightText, setPendingHighlightText] = useState('');

  const handlePreviousHighlight = (e) => {
    if (e) e.preventDefault();
    if (numberOfHighlights === 1) {
      setHighlightedIndex(0);
      setTimeout(() => setHighlightedIndex(1), 0);
    } else {
      setHighlightedIndex(highlightedIndex > 1 ? highlightedIndex - 1 : numberOfHighlights);
    }
  };
  
  const handleNextHighlight = (e) => {
    if (e) e.preventDefault();
    if (numberOfHighlights === 1) {
      setHighlightedIndex(0);
      setTimeout(() => setHighlightedIndex(1), 0);
    } else {
      setHighlightedIndex(highlightedIndex < numberOfHighlights ? highlightedIndex + 1 : 1);
    }
  };

  const handleMiddlePanelMouseUp = () => {
    const selection = window.getSelection();
    const text = selection ? selection.toString().trim() : '';
    if (text && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setPendingHighlightText(text);
      setHighlightBtnPos({ x: rect.left + rect.width / 2, y: rect.top });
    } else {
      setHighlightBtnPos(null);
      setPendingHighlightText('');
    }
  };

  useEffect(() => {
    const dismiss = (e) => {
      if (!e.target.closest('[data-highlight-btn-rsv]')) {
        setHighlightBtnPos(null);
      }
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, []);

  const rightContentRef = useRef(null);
  const currentFile = useMemo(() => files[currentFileIndex], [files, currentFileIndex]);

  useEffect(() => {
    if (hasMounted.current) { return; }
    if(selectedReport) {
      setReportTitle(selectedReport.title);
    }
    hasMounted.current = true;
  }, []);


  useEffect(() => {
    const retrieveText = async () => {
      try {
        const retrievedText = await handleRetrieveText(selectedText);
        setRetrieving(false);
        
        // Add null/undefined check and ensure it's an array
        if (retrievedText && Array.isArray(retrievedText) && retrievedText.length > 0) {
          highlightContent(files[currentFileIndex]?.content || "", retrievedText);
          setFileHighlightCounts(getFileHighlightCounts(retrievedText));
          settingNumberOfHighlights(retrievedText);
        } else {
          // Handle empty/null results
          setNumberOfHighlights(0);
          setFileHighlightCounts(getFileHighlightCounts([]));
          setContent(files[currentFileIndex]?.content || "");
        }
      } catch (error) {
        console.error('Error retrieving text:', error);
        setRetrieving(false);
        setNumberOfHighlights(0);
        setFileHighlightCounts(getFileHighlightCounts([]));
      }
    };
    
    if (selectedText !== "") {
      setHighlightingProgress(true);
      setRetrieving(true);
      retrieveText();
    } else {
      setNumberOfHighlights(0);
      setFileHighlightCounts(getFileHighlightCounts([]));
      setContent(files[currentFileIndex]?.content || "");
    }
  }, [selectedText]);

  useEffect(() => {
    highlightContent(currentFile?.content || "", retrievedData);
    setFileHighlightCounts(getFileHighlightCounts(retrievedData));
    settingNumberOfHighlights(retrievedData);
  }, [currentFile, retrievedData]);

  useEffect(() => {
    if(!highlightingProgress && numberOfHighlights > 0) {
      setHighlightedIndex(1);
    }
  }, [highlightingProgress]);

  const settingNumberOfHighlights = (retrievedText) => {
    if (!retrievedText || !Array.isArray(retrievedText)) {
      setNumberOfHighlights(0);
      return;
    }

    if (!files[currentFileIndex]?.content || !selectedText) {
      setNumberOfHighlights(0);
      return;
    }
    
    // Create a temporary DOM element with the file content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = files[currentFileIndex].content;
    
    // Extract all text nodes from the DOM
    const textNodes = [];
    const highlightedSentences = new Set();
    let count = 0;
    
    const walkNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim() !== '') {
          textNodes.push(node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        node.childNodes.forEach(walkNode);
      }
    };
    
    walkNode(tempDiv);
    
    // Check each text node against retrieved text
    textNodes.forEach((textNode) => {
      const normalizedSentence = textNode.textContent.replace(/\s+/g, " ").trim();
      if (!normalizedSentence || highlightedSentences.has(normalizedSentence)) return;
      if (isSourceSentence(normalizedSentence, retrievedText, selectedText)) {
        count++;
        highlightedSentences.add(normalizedSentence);
      }
    });

    setNumberOfHighlights(count);
  };
  
  const getFileHighlightCounts = (retrievedText) => {
    if (!retrievedText || !Array.isArray(retrievedText)) {
      return files.map(file => ({ name: file.name, count: 0 }));
    }
    return files.map((file) => {
      if (!file.content || !selectedText) {
        return { name: file.name, count: 0 };
      }
      
      // Create a temporary DOM element with the file content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = file.content;
      
      // Extract all text nodes from the DOM
      const textNodes = [];
      const highlightedSentences = new Set();
      let count = 0;
      
      const walkNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (node.textContent.trim() !== '') {
            textNodes.push(node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          node.childNodes.forEach(walkNode);
        }
      };
      
      walkNode(tempDiv);
      
      // Check each text node against retrieved text
      textNodes.forEach((textNode) => {
        const normalizedSentence = textNode.textContent.replace(/\s+/g, " ").trim();
        if (!normalizedSentence || highlightedSentences.has(normalizedSentence)) return;
        if (isSourceSentence(normalizedSentence, retrievedText, selectedText)) {
          count++;
          highlightedSentences.add(normalizedSentence);
        }
      });

      return { name: file.name, count: selectedText ? count : 0 };
    });
  };

  const highlightContent = (content, retrievedText) => {
    const currentFile = files[currentFileIndex];
    // Add array check
    if (!retrievedText || !Array.isArray(retrievedText) || retrievedText.length === 0) {
      setContent(content);
      setHighlightingProgress(false);
      return;
    }
    // Skip highlighting for non-HTML content types
    if (currentFile && (
      currentFile.name.endsWith('.png') ||
      currentFile.name.endsWith('.jpg') ||
      currentFile.name.endsWith('.jpeg') ||
      currentFile.name.endsWith('.csv') ||
      currentFile.name.endsWith('.xlsx')
    )) {
      setHighlightingProgress(false);
      return;
    }

    // Create a temporary DOM element to work with the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    // Extract text nodes that need highlighting
    const textNodes = [];
    const walkNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (node.textContent.trim() !== '') {
          textNodes.push(node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        node.childNodes.forEach(walkNode);
      }
    };
    walkNode(tempDiv);

    // Drop segments that cannot point at one passage: fragments under 4 chars, and a
    // segment that is most of the document (the model echoing it back), which would
    // otherwise light up every line. Mirrors the backend sanitiser.
    const documentLength = tempDiv.textContent.replace(/\s+/g, " ").trim().length;
    const usableSegments = retrievedText.filter((text) => {
      const len = (typeof text === 'string' ? text : '').replace(/\s+/g, " ").trim().length;
      return len >= 4 && !(documentLength >= 200 && len > documentLength * 0.5);
    });

    // Process each text node for highlighting
    let currentHighlightIndex = 0;
    const highlightedSentences = new Set();

    textNodes.forEach((textNode) => {
      const normalizedSentence = textNode.textContent.replace(/\s+/g, " ").trim();
      if (!normalizedSentence) return;

      let isHighlighted = false;

      if (!highlightedSentences.has(normalizedSentence)
          && isSourceSentence(normalizedSentence, usableSegments, selectedText)) {
        isHighlighted = true;
        highlightedSentences.add(normalizedSentence);
      }

      if (isHighlighted && selectedText !== "") {
        currentHighlightIndex++;
        const highlightColor = generateHighlightColor(currentHighlightIndex);
        
        // Create a mark element to replace the text node
        const markElement = document.createElement('mark');
        markElement.dataset.index = currentHighlightIndex;
        markElement.dataset.color = highlightColor;
        markElement.style.backgroundColor = highlightColor;
        markElement.textContent = textNode.textContent;
        
        // Replace the text node with our marked version
        textNode.parentNode.replaceChild(markElement, textNode);
      }
    });
    setContent(tempDiv.innerHTML);
    setHighlightingProgress(false);
    if (currentHighlightIndex > 0) {
      setHighlightedIndex(1);
    }
  };

  const generateHighlightColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };



  // Image Preview Component
  const ImagePreview = React.memo(({ file }) => {
    const [imageSrc, setImageSrc] = useState('');

    useEffect(() => {
      if (file && (file.name.endsWith('.png') || file.name.endsWith('.jpg') || file.name.endsWith('.jpeg'))) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageSrc(e.target.result);
        };
        reader.readAsDataURL(file);
      }
    }, [file]);

    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        width: '100%', 
        height: '100%', 
        overflow: 'auto' 
      }}>
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt="File preview" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '100%', 
              objectFit: 'contain' 
            }} 
          />
        ) : (
          <Typography>Loading image...</Typography>
        )}
      </Box>
    );
  });


  // Excel/CSV Preview Component
  const SpreadsheetPreview = React.memo(({ file }) => {
    const [tableData, setTableData] = useState({ headers: [], rows: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const parseFile = async () => {
        try {
          setLoading(true);
          
          if (file.name.endsWith('.csv')) {
            // Parse CSV
            const text = await file.text();
            Papa.parse(text, {
              header: true,
              complete: (results) => {
                if (results.data.length > 0) {
                  setTableData({
                    headers: Object.keys(results.data[0]),
                    rows: results.data
                  });
                }
                setLoading(false);
              }
            });
          } else if (file.name.endsWith('.xlsx')) {
            // Parse Excel
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: 'buffer' });
            const sheetName = wb.SheetNames[0];
            const ws = wb.Sheets[sheetName];
            const data = XLSX.utils.sheet_to_json(ws);
            
            if (data.length > 0) {
              setTableData({
                headers: Object.keys(data[0]),
                rows: data
              });
            }
            setLoading(false);
          }
        } catch (error) {
          console.error('Error parsing spreadsheet:', error);
          setLoading(false);
        }
      };

      if (file) {
        parseFile();
      }
    }, [file]);

    if (loading) {
      return <Typography>Loading spreadsheet...</Typography>;
    }

    return (
      <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
        {tableData.rows.length > 0 ? (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {tableData.headers.map((header, idx) => (
                  <th key={idx} style={{
                    padding: '8px',
                    backgroundColor: '#f2f2f2',
                    border: '1px solid #ddd',
                    position: 'sticky',
                    top: 0,
                    textAlign: 'left'
                  }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {tableData.headers.map((header, colIdx) => (
                    <td key={colIdx} style={{
                      padding: '8px',
                      border: '1px solid #ddd',
                      textAlign: 'left'
                    }}>
                      {row[header]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Typography>No data found in spreadsheet</Typography>
        )}
      </Box>
    );
  });


const PDFPreview = React.memo(({ file }) => {
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Import styles at top of your file: import '@react-pdf-viewer/core/lib/styles/index.css';

  React.useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        if (file) {
          // Create a URL for the file
          const url = URL.createObjectURL(file);
          setPdfUrl(url);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF file');
        setLoading(false);
      }
    };

    loadPdf();
    
    // Cleanup URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [file]);

  if (loading) {
    return <Typography>Loading PDF...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      {pdfUrl && (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <Viewer
            fileUrl={pdfUrl}
            defaultScale={1.0}
            theme={{
              theme: {
                typography: {
                  fontFamily: 'Arial, sans-serif',
                },
              },
            }}
            plugins={[]}
            />
        </Worker>
        )}
      </Box>
    );
  });

  const isPDFEncrypted = async (file) => {
    try {
      if (!file || !file.name.endsWith('.pdf')) {
        return false;
      }

      // Read the first few bytes of the PDF to check the header and encryption markers
      const chunk = file.slice(0, 2048); // Read first 2KB
      const arrayBuffer = await chunk.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Convert to string for pattern matching
      const pdfHeader = new TextDecoder('latin1').decode(uint8Array);
      
      // Check for encryption indicators in PDF structure
      const encryptionIndicators = [
        '/Encrypt',           // Direct encryption dictionary reference
        '/Filter/Standard',   // Standard security handler
        '/Filter/V2',         // Version 2 encryption
        '/V 1',              // Encryption version 1
        '/V 2',              // Encryption version 2
        '/V 4',              // Encryption version 4
        '/V 5',              // Encryption version 5
        '/R 2',              // Revision 2 encryption
        '/R 3',              // Revision 3 encryption
        '/R 4',              // Revision 4 encryption
        '/R 5',              // Revision 5 encryption
        '/R 6',              // Revision 6 encryption
        '/P -',              // Permissions (negative values indicate restrictions)
        'endobj' + String.fromCharCode(10) + '/Encrypt', // Encryption object
      ];
      
      // Check if any encryption indicators are present
      const hasEncryptionMarkers = encryptionIndicators.some(indicator => 
        pdfHeader.includes(indicator)
      );
      
      return hasEncryptionMarkers;
      
    } catch (error) {
      console.error('Error checking PDF encryption:', error);
      // If we can't read the file, assume it might be encrypted
      return true;
    }
  };


  const FileViewer = useMemo(() => {
    if (!currentFile) return null;

    if (currentFile.name.endsWith('.png') || 
        currentFile.name.endsWith('.jpeg') ||
        currentFile.name.endsWith('.jpg')) {
      return <ImagePreview file={currentFile.originalData} />;
    } 
    else if (currentFile.name.endsWith('.csv') || 
             currentFile.name.endsWith('.xlsx')) {
      return <SpreadsheetPreview file={currentFile.originalData} />;
    } 
    else if (currentFile.name.endsWith('.pdf') && (!content || isPDFEncrypted(currentFile.originalData))) {
      return <PDFPreview file={currentFile.originalData} />;
    }
    else {
      return (
        <TemplateView 
          handleBack={null}
          templateId={null}
          userDetails={null}
          view="summarizer"
          reportContent={content}
          highlightedIndex={highlightedIndex}
          currentFileIndex={currentFileIndex}
          contentRef={rightContentRef}
          containerId="template-view-container"
        />
      );
    }
  }, [currentFile, content, highlightedIndex, currentFileIndex]);

  return (
    <Grid container spacing={2} sx={{ flexDirection: { xs: "column", md: "row"}, paddingBottom: "20px",}}>
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <Split
          sizes={[50, 50]}
          minSize={[300, 300]}
          gutterSize={20}
          gutter={createSplitGutter}
          gutterAlign="center"
          direction="horizontal"
          cursor="col-resize"
          snapOffset={0}
          style={{
            display: 'flex',
            width: '100%',
            height: '100vh', 
          }}
          onDrag={() => {}}
        >
          {/* Left Pane - Files */}
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              display: 'flex'
            }}
          >
            <Grid
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                padding: 2,
                borderRadius: 2,
                height: '100%',
                overflow: 'hidden'
              }}
              ref={rightContentRef} 
            >
              <Typography variant="h6" sx={{color: "#29C0BB", fontWeight: 'bold'}}>Files</Typography>
              <Stack direction="row" justifyContent="space-between" sx={{mb: 2, mt: 2}}>
                <Typography variant="h6" 
                  sx={{mr: 3,
                    maxWidth: "70%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    }}>
                  {files[currentFileIndex]?.name || "No File Selected"}
                </Typography>
                <div sx={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Move to previous source">
                    <IconButton onClick={(e) => handlePreviousHighlight(e)}>
                      <ArrowUpward />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move to next source">
                    <IconButton onClick={(e) => handleNextHighlight(e)}>
                      <ArrowDownward />
                    </IconButton>
                  </Tooltip>
                </div>
              </Stack>

              <Box sx={{ mb: 2 }}>
                {fileHighlightCounts.map(({ name, count }, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      marginBottom: "4px",
                      backgroundColor: count > 0 ? "rgba(0, 128, 128, 0.1)" : '#f5f5f5',
                      cursor: 'pointer'
                    }}
                    onClick={() => { setCurrentFileIndex(idx); }}
                  >
                    <Typography               
                    sx={{
                      maxWidth: "70%",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                      {name}
                    </Typography>
                    <Typography variant="caption">
                      {count > 0 ? `${count} sources` : "No sources"}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  overflow: "auto",
                  padding: 0,
                  position: "relative",
                  "&:focus-within": {
                    outline: "none"
                  }
                }}
                id="template-view-container"
              >
                {FileViewer}
              </Box>
            </Grid>
          </Box>

          {/* Middle Pane - Report */}
          <Box
            sx={{
              height: '100%',
              overflow: 'hidden',
              display: 'flex'
            }}
          >
            <Grid
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                padding: 2,
                borderRadius: 2,
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Stack direction="column" alignItems="stretch" spacing={1.5} sx={{ marginBottom: '16px' }}>
                <Typography
                  variant="h5"
                  noWrap
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {selectedReport.title}
                </Typography>
                <Stack 
                  direction="row" 
                  alignItems="center" 
                  sx={{ 
                    mt: 0.5, 
                    flexShrink: 0, 
                    justifyContent: 'space-between', 
                    width: '100%',
                    flexWrap: 'wrap',
                    gap: 1.5 
                  }}
                >
                  {/* Template dropdown */}
                  <Select
                    size="small"
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const t = (templates || []).find(tmpl => tmpl.id === e.target.value);
                      if (t) handleTemplateSelect(t);
                    }}
                    displayEmpty
                    sx={{ minWidth: 160, maxWidth: 260, fontSize: '0.875rem' }}
                  >
                    <MenuItem disabled value="">
                      <em>{!templates || templates.length === 0 ? "No templates" : "Select Template"}</em>
                    </MenuItem>
                    {templates && templates.map(t => (
                      <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                    ))}
                  </Select>

                  <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {/* Upload template */}
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    sx={{
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      borderColor: '#D1D5DB',
                      color: '#374151',
                      '&:hover': { borderColor: '#9CA3AF', backgroundColor: '#F9FAFB' },
                    }}
                  >
                    Upload Template
                    <input type="file" hidden accept=".txt,.docx,.pdf" onChange={handleUploadTemplate} />
                  </Button>
                  {/* Generate */}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={isGeneratingReport ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    disabled={!selectedTemplate || isGeneratingReport}
                    onClick={() => regenerateHistoryReport()}
                    sx={{
                      backgroundColor: '#7C3AED',
                      '&:hover': { backgroundColor: '#6D28D9' },
                      '&.Mui-disabled': { backgroundColor: '#DDD6FE', color: '#7C3AED' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                      px: 1.5,
                    }}
                  >
                    {isGeneratingReport ? 'Generating...' : 'Generate'}
                  </Button>
                  </Stack>
                </Stack>
              </Stack>
              <Box
                onMouseUp={handleMiddlePanelMouseUp}
                onTouchEnd={handleMiddlePanelMouseUp}
                sx={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <TemplateView onTemplateSaved={onTemplateSaved}
                  handleBack={handleBackToTemplateSelector}
                  reportId={selectedReport.id}
                  reportContent={selectedReport.originalData.content}
                  userDetails={userDetails}
                  view="report"
                  edit={true}
                  setEdit={null}
                  setSelectedText={setSelectedText}
                  reportTitle={reportTitle}
                  setHistoryItems={setHistoryItems}
                  setSelectedReport={setSelectedReport}
                  setSectionSummary={null}
                  selectedReport={selectedReport}
                  currentOrg={currentOrg}
                  patient_id={patient_id}
                  highlightMode={highlightMode}
                />
              </Box>

              {/* Floating Highlight Button — appears above selected text in the middle pane */}
              {highlightBtnPos && (
                <Portal>
                  <Box
                    data-highlight-btn-rsv="true"
                    sx={{
                      position: 'fixed',
                      left: highlightBtnPos.x,
                      top: highlightBtnPos.y - 44,
                      transform: 'translateX(-50%)',
                      zIndex: 9999,
                      bgcolor: '#1C1C2E',
                      borderRadius: '8px',
                      px: 1.5,
                      py: 0.75,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderWidth: '6px 6px 0',
                        borderStyle: 'solid',
                        borderColor: '#1C1C2E transparent transparent',
                      },
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedText(pendingHighlightText);
                        setHighlightBtnPos(null);
                        setPendingHighlightText('');
                      }}
                      startIcon={retrieving
                        ? <CircularProgress size={12} color="inherit" />
                        : <HighlightIcon sx={{ fontSize: 14 }} />
                      }
                      disabled={retrieving}
                      sx={{
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontSize: 12,
                        fontWeight: 600,
                        minWidth: 0,
                        p: 0,
                        '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
                      }}
                    >
                      {retrieving ? 'Finding Sources...' : 'Check Sources'}
                    </Button>
                  </Box>
                </Portal>
              )}
            </Grid>
          </Box>

        </Split>
      </Box>
      {
        retrieving &&
        <PopupMenu>
          <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
            <Typography variant="h6">{'Retrieving Text'}</Typography>
            <Loader />
          </Stack>
        </PopupMenu>
      }
    </Grid>
  );
};

export default ReportSummaryView;
